/**
 * Cầu nối liên kết học liệu ↔ node canvas (round-trip create-new).
 *
 * Luồng:
 *  1. Tại node, GV bấm "Tạo học liệu này" → setPendingLink({canvasId,nodeId,resourceType})
 *     rồi điều hướng sang trình tạo (giáo án/quiz/slide…).
 *  2. Trình tạo lưu resource → apiService.syncResource ghi breadcrumb
 *     `eduapp_last_created_resource` ({resource,id,title,ts}).
 *  3. Khi GV quay lại canvas, consumePendingLink() đối chiếu breadcrumb với
 *     pending link (cùng resourceType, ts mới hơn) → trả về {nodeId,resourceId,title}
 *     để canvas gắn resourceId vào đúng node, rồi xóa cả hai dấu vết.
 *
 * Canvas Return Context:
 *  Khi GV mở xem học liệu từ canvas, hệ thống lưu context "quay về" để trang học liệu
 *  có thể hiển thị nút "← Quay về bài giảng" giúp GV điều hướng ngược lại.
 */

const PENDING_KEY = 'eduapp_canvas_pending_link';
const CRUMB_KEY = 'eduapp_last_created_resource';

export interface PendingLink {
  canvasId?: string;
  nodeId: string;
  resourceType: string;
  ts: number;
}

interface CreatedCrumb {
  resource: string;
  id: string;
  title?: string;
  ts: number;
}

export function setPendingLink(link: Omit<PendingLink, 'ts'>): void {
  try {
    // Xóa breadcrumb cũ để không khớp nhầm với resource tạo trước đó.
    localStorage.removeItem(CRUMB_KEY);
    localStorage.setItem(PENDING_KEY, JSON.stringify({ ...link, ts: Date.now() }));
  } catch { /* ignore */ }
}

export function clearPendingLink(): void {
  try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
}

/**
 * Trả về {nodeId, resourceId, title} nếu có học liệu vừa tạo khớp pending link.
 * Đồng thời dọn dẹp pending link + breadcrumb. Null nếu không khớp.
 */
export function consumePendingLink():
  | { nodeId: string; resourceId: string; resourceType: string; title?: string }
  | null {
  try {
    const pendingRaw = localStorage.getItem(PENDING_KEY);
    const crumbRaw = localStorage.getItem(CRUMB_KEY);
    if (!pendingRaw || !crumbRaw) return null;

    const pending = JSON.parse(pendingRaw) as PendingLink;
    const crumb = JSON.parse(crumbRaw) as CreatedCrumb;

    // Khớp khi: cùng resourceType và resource được tạo SAU khi đặt pending.
    if (crumb.resource === pending.resourceType && crumb.ts >= pending.ts) {
      localStorage.removeItem(PENDING_KEY);
      localStorage.removeItem(CRUMB_KEY);
      return {
        nodeId: pending.nodeId,
        resourceId: crumb.id,
        resourceType: crumb.resource,
        title: crumb.title,
      };
    }
  } catch { /* ignore */ }
  return null;
}

// ── Canvas Return Context ──────────────────────────────────────────────────────

const RETURN_CTX_KEY = 'eduapp_canvas_return_ctx';

/** Ngữ cảnh "quay về canvas" lưu khi GV mở học liệu từ node. */
export interface CanvasReturnContext {
  canvasId: string;
  canvasTitle: string;
  /** Path đầy đủ để quay về canvas: /workspace/giao-vien/{id} */
  returnPath: string;
  /** ID node để highlight khi quay về (tùy chọn). */
  nodeId?: string;
  nodeLabel?: string;
  ts: number;
}

/** Lưu context quay về canvas trước khi điều hướng sang trang học liệu. */
export function setCanvasReturnContext(ctx: Omit<CanvasReturnContext, 'ts'>): void {
  try {
    localStorage.setItem(RETURN_CTX_KEY, JSON.stringify({ ...ctx, ts: Date.now() }));
  } catch { /* ignore */ }
}

/**
 * Đọc context quay về canvas. Tự hủy nếu quá 4 giờ (stale).
 * Trả về null nếu không có hoặc đã hết hạn.
 */
export function getCanvasReturnContext(): CanvasReturnContext | null {
  try {
    const raw = localStorage.getItem(RETURN_CTX_KEY);
    if (!raw) return null;
    const ctx = JSON.parse(raw) as CanvasReturnContext;
    if (Date.now() - ctx.ts > 4 * 60 * 60 * 1000) {
      localStorage.removeItem(RETURN_CTX_KEY);
      return null;
    }
    return ctx;
  } catch { /* ignore */ }
  return null;
}

/** Xóa context quay về canvas (sau khi user đã quay về hoặc dismiss). */
export function clearCanvasReturnContext(): void {
  try { localStorage.removeItem(RETURN_CTX_KEY); } catch { /* ignore */ }
}
